import Text from '@atoms/Text/Text';
import { HeroVariantEnum, IArticleHeader } from '@models/IHero';
import { HeroMedia } from '@molecules/HeroMedia/HeroMedia';
import backgroundColours from '@utility/config/backgroundColours';
import { brightnessByColour } from '@utility/helpers/brightnessByColour';
import classNames from 'classnames';

const ArticleHeader = (props: IArticleHeader) => {
  const {
    backgroundColour,
    backgroundImage,
    deactivateAutoplay,
    hasPlayButton,
    imageBlock,
    subText,
    title,
    trumpetText,
    variant,
    video,
  } = props;

  if (!title && !video && !imageBlock?.image) return null;

  const bgHexCode = backgroundColour?.includes('#')
    ? backgroundColour
    : backgroundColour && backgroundColours?.[backgroundColour];
  // Horizontal desktop layout is when the subtext appears adjacent to the title rather than below it
  const isHorizontalDesktopLayout =
    variant === HeroVariantEnum.Stacked ||
    variant === HeroVariantEnum.Full ||
    (!video && !imageBlock?.image);

  const darkFont = brightnessByColour(bgHexCode || '#ffffff') > 127;

  return (
    <section
      className="article-hero"
      style={{ backgroundColor: bgHexCode ?? '' }}
    >
      <div
        className={classNames('tw-relative tw-flex tw-flex-col ', {
          'lg:tw-flex-row': variant === HeroVariantEnum.Split,
          'lg:tw-flex-row-reverse': variant === HeroVariantEnum.SplitReverse,
          'lg:tw-h-[608px] xl:tw-min-h-[75vh]': !isHorizontalDesktopLayout,
        })}
      >
        {(title || subText) && (
          <div
            className={classNames(
              'tw-relative tw-z-[1] tw-grid tw-h-full tw-grid-cols-12 tw-px-4 tw-py-10 tw-text-left md:tw-px-12 rtl:tw-text-right',
              darkFont ? 'tw-text-grey-100' : 'tw-text-white',
              isHorizontalDesktopLayout
                ? 'tw-mx-auto tw-w-full tw-max-w-screen-xxl md:tw-py-[120px]'
                : 'md:tw-max-w-[800px] lg:tw-min-h-[608px] lg:tw-basis-1/2 ',
              {
                'lg:tw-ml-auto':
                  variant === HeroVariantEnum.Split &&
                  !isHorizontalDesktopLayout,
                'lg:tw-mr-auto':
                  variant === HeroVariantEnum.SplitReverse &&
                  !isHorizontalDesktopLayout,
              }
            )}
          >
            <div
              className={classNames(
                'tw-col-span-12 tw-mb-[90px] md:tw-mb-16',
                isHorizontalDesktopLayout && 'lg:tw-col-span-6 lg:tw-mb-0'
              )}
            >
              {trumpetText ? (
                <Text
                  variant="body-small"
                  className="tw-mb-2 tw-uppercase md:tw-mb-4"
                >
                  {trumpetText}
                </Text>
              ) : null}

              {title ? (
                <Text variant="hero-headline" as="h1">
                  {title}
                </Text>
              ) : null}
            </div>

            {subText ? (
              <Text
                variant="heading-21-light"
                className={classNames(
                  'tw-col-span-12',
                  isHorizontalDesktopLayout
                    ? 'lg:tw-col-span-4 lg:tw-col-start-9'
                    : 'tw-mt-auto xl:tw-col-span-8',
                  isHorizontalDesktopLayout && trumpetText && 'tw-pt-4'
                )}
              >
                {subText}
              </Text>
            ) : null}
          </div>
        )}
        {/* Image Layout */}
        {imageBlock?.image && !video ? (
          <div
            className={classNames(
              'tw-relative tw-h-[468px] ',
              isHorizontalDesktopLayout
                ? 'tw-max-w-full lg:tw-aspect-video lg:tw-h-auto lg:tw-max-h-[75vh]'
                : 'lg:tw-h-full lg:tw-basis-1/2'
            )}
          >
            <HeroMedia
              className={'tw-absolute tw-inset-0'}
              darkFont={darkFont}
              isSplit={!isHorizontalDesktopLayout}
              image={{
                ...imageBlock?.image,
                altText: imageBlock?.alternativeText,
              }}
              {...props}
            />
          </div>
        ) : null}
        {/* Video Variant */}
        {video ? (
          <div
            className={classNames(
              'tw-relative tw-aspect-video tw-w-full',
              isHorizontalDesktopLayout
                ? 'tw-max-h-[75vh]'
                : 'lg:tw-aspect-auto lg:tw-h-full lg:tw-w-auto lg:tw-basis-1/2'
            )}
          >
            <HeroMedia
              className="tw-absolute tw-inset-0"
              darkFont={darkFont}
              isSplit={
                variant === HeroVariantEnum.Split ||
                variant === HeroVariantEnum.SplitReverse
              }
              autoPlay={!deactivateAutoplay}
              muted={true}
              hasQBrickPlayButton={hasPlayButton}
              image={
                imageBlock?.image
                  ? {
                      ...imageBlock?.image,
                      altText: imageBlock?.alternativeText,
                    }
                  : undefined
              }
              videoPoster={backgroundImage}
              {...props}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default ArticleHeader;
